import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../UI/Button';
import { formatMad } from '../../utils/currency';

const BookingPriceSummary = ({
  selectedVilla,
  bookingData,
  quote,
  quoteLoading,
  quoteError,
  servicesTotal,
  totalPrice,
  onContinue,
  continueDisabled,
  continueLabel,
  showContinue = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const nights = quote?.nights ?? (
    bookingData.checkIn && bookingData.checkOut
      ? Math.ceil(
          (new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) /
            (1000 * 60 * 60 * 24)
        )
      : 0
  );

  const groupedNightlyRates = (quote?.nightly_breakdown || []).reduce((groups, night) => {
    const key = String(night.price);
    if (!groups[key]) {
      groups[key] = { ...night, count: 0 };
    }
    groups[key].count += 1;
    return groups;
  }, {});
  const nightlyGroups = Object.values(groupedNightlyRates);

  return (
    <div className="lg:sticky lg:top-24 rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
      {selectedVilla && !compact && (
        <img
          src={selectedVilla.image}
          alt={selectedVilla.name}
          className="w-full h-32 sm:h-44 object-cover"
        />
      )}

      <div className={`${compact ? 'p-4' : 'p-4 sm:p-6'} space-y-3 sm:space-y-4`}>
        <div>
          <h3 className="text-lg sm:text-xl font-sofia font-bold text-black">
            {selectedVilla?.name || t('booking.chooseVilla')}
          </h3>
          {compact && (
            <p className="text-sm font-sofia text-text-secondary mt-1">
              {t('booking.selectVillaHint', 'Select a villa to continue')}
            </p>
          )}
          {!compact && bookingData.checkIn && bookingData.checkOut && (
            <p className="text-sm font-sofia text-text-secondary mt-1">
              {bookingData.checkIn} → {bookingData.checkOut}
            </p>
          )}
        </div>

        {!compact && (
        <div className="border rounded-xl p-3 sm:p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3 text-sm font-sofia">
            <div>
              <div className="text-text-secondary">{t('booking.checkIn')}</div>
              <div className="font-medium">{bookingData.checkIn || '—'}</div>
            </div>
            <div>
              <div className="text-text-secondary">{t('booking.checkOut')}</div>
              <div className="font-medium">{bookingData.checkOut || '—'}</div>
            </div>
          </div>
          <div className="text-sm font-sofia space-y-1">
            <div>
              <span className="text-text-secondary">{t('booking.guestBreakdown.adults')}: </span>
              <span className="font-medium">{bookingData.guests?.adults ?? 0}</span>
            </div>
            {(bookingData.guests?.children ?? 0) > 0 && (
              <div>
                <span className="text-text-secondary">{t('booking.guestBreakdown.children')}: </span>
                <span className="font-medium">{bookingData.guests.children}</span>
              </div>
            )}
            {(bookingData.guests?.babies ?? 0) > 0 && (
              <div>
                <span className="text-text-secondary">{t('booking.guestBreakdown.babies')}: </span>
                <span className="font-medium">{bookingData.guests.babies}</span>
              </div>
            )}
            {(bookingData.guests?.pets ?? 0) > 0 && (
              <div>
                <span className="text-text-secondary">{t('booking.guestBreakdown.pets')}: </span>
                <span className="font-medium">{bookingData.guests.pets}</span>
              </div>
            )}
          </div>
        </div>
        )}

        {!compact && quoteLoading && (
          <div className="text-sm font-sofia text-[#81736d] animate-pulse">
            {t('booking.fetchingPrice', 'Checking availability and price...')}
          </div>
        )}

        {!compact && quoteError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm font-sofia text-red-700">
            {quoteError}
          </div>
        )}

        {!compact && quote && !quoteLoading && (
          <div className="space-y-2 text-sm font-sofia">
            {quote.available ? (
              <>
                {nightlyGroups.length > 0 ? (
                  nightlyGroups.map((group) => (
                    <div key={group.price} className="flex justify-between">
                      <span>
                        {formatMad(group.price)} x {group.count}{' '}
                        {t('booking.nightsLabel', 'nights')}
                      </span>
                      <span>{formatMad((group.price || 0) * group.count)}</span>
                    </div>
                  ))
                ) : (
                  quote.price_per_night != null &&
                  nights > 0 && (
                    <div className="flex justify-between">
                      <span>
                        {formatMad(quote.price_per_night)} x {nights}{' '}
                        {t('booking.nightsLabel', 'nights')}
                      </span>
                      <span>{formatMad(quote.total_price)}</span>
                    </div>
                  )
                )}
                {servicesTotal > 0 && (
                  <div className="flex justify-between">
                    <span>{t('booking.additionalServices')}</span>
                    <span>{formatMad(servicesTotal)}</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between text-base font-bold text-black">
                  <span>{t('booking.total')}</span>
                  <span className="text-[#81736d]">
                    {formatMad(totalPrice ?? quote.total_price)}
                  </span>
                </div>
              </>
            ) : (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-800">
                {quote.error || t('booking.datesUnavailable', 'These dates are not available.')}
                {quote.minimum_length_of_stay && (
                  <div className="mt-1">
                    {t('booking.minStay', 'Minimum stay')}: {quote.minimum_length_of_stay}{' '}
                    {t('booking.nightsLabel', 'nights')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!compact && !quote && !quoteLoading && bookingData.villa && bookingData.checkIn && bookingData.checkOut && (
          <p className="text-sm font-sofia text-text-secondary">
            {t('booking.selectDatesForPrice', 'Select valid dates to see live pricing from Smoobu.')}
          </p>
        )}

        {showContinue && (
          <Button className="w-full" onClick={onContinue} disabled={continueDisabled}>
            {continueLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BookingPriceSummary;
